import { useState, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import {
  Search,
  Loader2,
  AlertCircle,
  QrCode,
  CheckCircle,
  XCircle,
  Clock,
  Filter,
  ChevronRight,
} from 'lucide-react';
import { useSerializedUnits, type SerializedUnitListItem } from '../hooks/useSerialization';
import { AsBuiltViewer } from '../components/AsBuiltViewer';

const statusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'complete', label: 'Complete' },
  { value: 'scrapped', label: 'Scrapped' },
];

const statusConfig: Record<string, { label: string; color: string; icon: typeof CheckCircle }> = {
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700', icon: Clock },
  complete: { label: 'Complete', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  scrapped: { label: 'Scrapped', color: 'bg-red-100 text-red-700', icon: XCircle },
};

export function SerializedUnitsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedSerialNumber, setSelectedSerialNumber] = useState<string | null>(null);

  // Debounced search
  const searchParams = useMemo(() => ({
    search: searchQuery || undefined,
    status: statusFilter || undefined,
    limit: 50,
  }), [searchQuery, statusFilter]);

  const { data, isLoading, error, refetch } = useSerializedUnits(searchParams);

  const units = data?.data ?? [];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Serialized Units</h1>
        <p className="text-sm text-foreground-muted mt-1">
          Search and view as-built documentation for serialized units
        </p>
      </div>

      {/* Search and filters */}
      <div className="bg-background rounded-lg border border-border p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-[250px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted" />
            <input
              type="text"
              placeholder="Search by serial number or part number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {/* Status filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-foreground-muted" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Results count */}
        {data && (
          <p className="text-sm text-foreground-muted mt-3">
            Found {data.total ?? units.length} serialized unit{units.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-danger bg-danger-subtle p-6">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-danger" />
            <div>
              <h3 className="font-medium text-danger-foreground">
                Failed to load serialized units
              </h3>
              <p className="text-sm text-danger-foreground mt-1">
                {error instanceof Error ? error.message : 'Unknown error'}
              </p>
            </div>
          </div>
          <button
            onClick={() => refetch()}
            className="mt-4 text-sm font-medium text-danger hover:underline"
          >
            Try again
          </button>
        </div>
      ) : units.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-background p-8 text-center">
          <QrCode className="h-8 w-8 text-foreground-muted mx-auto mb-3" />
          <h3 className="text-lg font-medium text-foreground">No serialized units found</h3>
          <p className="text-sm text-foreground-muted mt-1">
            {searchQuery || statusFilter
              ? 'Try adjusting your search or filters'
              : 'Serialized units will appear here when created'}
          </p>
        </div>
      ) : (
        <div className="bg-background rounded-lg border border-border overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-background-subtle border-b border-border text-xs font-medium text-foreground-muted uppercase tracking-wider">
            <div className="col-span-3">Serial Number</div>
            <div className="col-span-2">Part Number</div>
            <div className="col-span-2">Sales Order</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2">Created</div>
            <div className="col-span-1"></div>
          </div>

          {/* Table body */}
          <div className="divide-y divide-border">
            {units.map((unit) => (
              <SerializedUnitRow
                key={unit.id}
                unit={unit}
                onClick={() => setSelectedSerialNumber(unit.serialNumber)}
              />
            ))}
          </div>
        </div>
      )}

      {/* As-Built Viewer Modal */}
      {selectedSerialNumber && (
        <AsBuiltViewer
          serialNumber={selectedSerialNumber}
          onClose={() => setSelectedSerialNumber(null)}
        />
      )}
    </div>
  );
}

interface SerializedUnitRowProps {
  unit: SerializedUnitListItem;
  onClick: () => void;
}

const defaultStatusConfig = { label: 'Unknown', color: 'bg-gray-100 text-gray-700', icon: AlertCircle };

function SerializedUnitRow({ unit, onClick }: SerializedUnitRowProps) {
  const config = statusConfig[unit.status] ?? defaultStatusConfig;
  const StatusIcon = config.icon;

  return (
    <button
      onClick={onClick}
      className="w-full grid grid-cols-12 gap-4 px-4 py-3 text-left hover:bg-background-subtle transition-colors"
    >
      <div className="col-span-3">
        <span className="font-mono font-semibold text-foreground">{unit.serialNumber}</span>
      </div>
      <div className="col-span-2">
        <span className="text-foreground">{unit.partNumber}</span>
      </div>
      <div className="col-span-2">
        <span className="text-foreground-muted">{unit.gpSalesOrderNumber ?? '-'}</span>
      </div>
      <div className="col-span-2">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
          <StatusIcon className="h-3 w-3" />
          {config.label}
        </span>
      </div>
      <div className="col-span-2">
        <span className="text-sm text-foreground-muted">
          {format(parseISO(unit.createdAt), 'MMM d, yyyy')}
        </span>
      </div>
      <div className="col-span-1 flex justify-end">
        <ChevronRight className="h-5 w-5 text-foreground-muted" />
      </div>
    </button>
  );
}
